// Browser-local persistence for datasets and items created in Ops → Datasets, so imported
// calls and corrected goldens survive a reload and are visible to Ops → Evals (a separate
// route with its own provider). A stopgap until the backend persists datasets (plan §8
// dep #3): it lives in this browser only and is never sent anywhere. Golden edits to the
// built-in mock items stay in memory for the session.
//
// Stored per workspace, so switching workspace never shows another workspace's calls.
// Without a workspace nothing is read or saved and only the mock fixtures show.

import { logError } from 'lib/logging'
import { MOCK_DATASETS, MOCK_ITEMS, type Dataset, type DatasetItem } from './mockData'

const STORAGE_KEY_PREFIX = `ops-datasets:local:v1:`

export const LOCAL_DATASET_PREFIX = `ds_local_`
export const LOCAL_ITEM_PREFIX = `item_local_`

type LocalDatasets = { datasets: Dataset[]; items: DatasetItem[] }

/** The mock fixtures plus everything created locally, as both Ops pages should see them. */
export function loadAllDatasets(workspaceId: string | undefined): LocalDatasets {
  let local: LocalDatasets = { datasets: [], items: [] }
  try {
    const raw = workspaceId && window.localStorage.getItem(`${STORAGE_KEY_PREFIX}${workspaceId}`)
    if (raw) local = JSON.parse(raw) as LocalDatasets
  } catch (error) {
    logError(`Could not read local ops datasets`, error)
  }
  return { datasets: [...MOCK_DATASETS, ...local.datasets], items: [...local.items, ...MOCK_ITEMS] }
}

/** Persist the locally created datasets and items; mock fixtures are never written. */
export function saveLocalDatasets(workspaceId: string | undefined, datasets: Dataset[], items: DatasetItem[]): void {
  if (!workspaceId) return
  const local: LocalDatasets = {
    datasets: datasets.filter((dataset) => dataset.id.startsWith(LOCAL_DATASET_PREFIX)),
    items: items.filter((item) => item.id.startsWith(LOCAL_ITEM_PREFIX)),
  }
  try {
    window.localStorage.setItem(`${STORAGE_KEY_PREFIX}${workspaceId}`, JSON.stringify(local))
  } catch (error) {
    logError(`Could not save local ops datasets`, error)
  }
}
